import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { getPagedCountries } from '@/services/country.service';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import { Country, CountryPaginationFilterRequest } from '@/models/country.model';
import { useNavigate } from 'react-router-dom';
import FetchingSelect from '@/components/FetchingSelect';
import { getContinentsLookup } from '@/services/continent.service';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const CountryList = () => {
  const [filterReq, setFilterReq] = useState<CountryPaginationFilterRequest>(new CountryPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: CountryPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<Country> = await getPagedCountries(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching countries:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/countries/new`)}>
          Add Country
        </Button>

        <Stack spacing={6}>
          <FetchingSelect
            label="Continent"
            fetchData={(text, page)=> getContinentsLookup(text, page)}
            value={filterReq.continentId}
            onChange={dataKey => setFilterReq(pre => ({...pre, continentId: dataKey}))}
          />             
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />    
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Icon</HeaderCell>
          <ImageCell dataKey="flagImageUrl" />
        </Column>              
        <Column flexGrow={0.5} align="left" fullText sortable>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Alpha Code (2)</HeaderCell>
          <Cell dataKey="alpha2Code" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Alpha Code (3)</HeaderCell>
          <Cell dataKey="alpha3Code" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Continent</HeaderCell>
          <Cell dataKey="continent.name" />
        </Column>
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>

          <Cell style={{ padding: '6px' }}>
            {(rowData : Country) => (              
              <IconButton onClick={() => nav(`/countries/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
      
    </PageContent>
  );
};

export default CountryList;
